import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import '../styles/navbar.css';
import '../styles/sidebar.css';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 16) {
      errors.push('Password must be at least 16 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return errors;
  };

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);
    setPasswordErrors(validatePassword(password));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password requirements
    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setError('Password does not meet requirements');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      const detail = err.response?.data?.detail;

      // Handle Pydantic validation errors (array of error objects)
      if (Array.isArray(detail)) {
        const errorMessages = detail.map((error: any) => error.msg || error.message).join(', ');
        setError(errorMessages || 'Validation error occurred');
      } else if (typeof detail === 'object' && detail.message) {
        setError(detail.message);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to reset password. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password: string): { label: string; color: string; width: string } => {
    const errors = validatePassword(password);
    if (password.length === 0) {
      return { label: '', color: '', width: '0%' };
    }
    if (errors.length === 0) {
      return { label: 'Strong', color: '#28a745', width: '100%' };
    }
    if (errors.length <= 2) {
      return { label: 'Medium', color: '#ffc107', width: '66%' };
    }
    return { label: 'Weak', color: '#dc3545', width: '33%' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <>
      {/* Header matching Login.tsx */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1040,
        backgroundColor: '#4D64C1',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        height: '70px'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 20px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <a href="https://purehd.com" target="_blank" rel="noopener noreferrer">
            <img
              src="/img/PureHD_Logo.png"
              alt="PureHD Logo"
              style={{
                height: '45px',
                width: 'auto',
                display: 'block',
                filter: 'brightness(0) invert(1)',
                objectFit: 'contain'
              }}
            />
          </a>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '30px'
          }}>
            <div style={{
              color: 'white',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-phone-alt" style={{ fontSize: '13px', opacity: 0.9 }}></i>
              <span>Questions? Call</span>
              <span style={{ fontWeight: '600' }}>(888) 787-3431</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        minHeight: '100vh',
        paddingTop: '70px',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="container" style={{ padding: '40px 15px' }}>
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-8 col-xl-5">
              <div className="mx-auto" style={{ maxWidth: '440px' }}>

                {!success ? (
                  <div className="card" style={{
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    border: 'none',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      backgroundColor: '#4D64C1',
                      color: 'white',
                      padding: '25px',
                      textAlign: 'center'
                    }}>
                      <i className="fas fa-lock" style={{ fontSize: '40px', marginBottom: '10px', opacity: 0.9 }}></i>
                      <h1 style={{ fontSize: '22px', margin: 0, fontWeight: '600' }}>Set New Password</h1>
                    </div>
                    <div className="card-body" style={{ padding: '35px 30px' }}>

                      {error && (
                        <div className="alert alert-danger d-flex align-items-center">
                          <i className="fas fa-times-circle flex-shrink-0 me-3"></i>
                          <div>{error}</div>
                        </div>
                      )}

                      <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                          <label htmlFor="newPassword" className="form-label" style={{ fontWeight: '500', color: '#2c3e50', marginBottom: '8px' }}>
                            New Password
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                              <i className="fas fa-lock" style={{ color: '#4D64C1' }}></i>
                            </span>
                            <input
                              type={showPassword ? "text" : "password"}
                              id="newPassword"
                              name="newPassword"
                              className="form-control"
                              required
                              autoFocus
                              placeholder="Enter new password"
                              value={newPassword}
                              onChange={(e) => handlePasswordChange(e.target.value)}
                              disabled={loading || !token}
                              style={{ borderLeft: 'none', borderRight: 'none' }}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              disabled={loading || !token}
                              style={{ borderLeft: 'none' }}
                            >
                              <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
                            </button>
                          </div>

                          {/* Password strength indicator */}
                          {newPassword && (
                            <div style={{ marginTop: '8px' }}>
                              <div style={{
                                height: '4px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '2px',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: passwordStrength.width,
                                  backgroundColor: passwordStrength.color,
                                  transition: 'all 0.3s ease'
                                }}></div>
                              </div>
                              <small style={{ color: passwordStrength.color, fontWeight: '500' }}>
                                {passwordStrength.label}
                              </small>
                            </div>
                          )}
                        </div>

                        {/* Password requirements */}
                        {newPassword && passwordErrors.length > 0 && (
                          <div className="mb-3">
                            <small style={{ color: '#6c757d', fontWeight: '500' }}>Password must contain:</small>
                            <ul style={{ fontSize: '12px', marginTop: '5px', marginBottom: 0, paddingLeft: '20px' }}>
                              {passwordErrors.map((err, index) => (
                                <li key={index} style={{ color: '#dc3545' }}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mb-4">
                          <label htmlFor="confirmPassword" className="form-label" style={{ fontWeight: '500', color: '#2c3e50', marginBottom: '8px' }}>
                            Confirm Password
                          </label>
                          <div className="input-group">
                            <span className="input-group-text" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                              <i className="fas fa-lock" style={{ color: '#4D64C1' }}></i>
                            </span>
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              id="confirmPassword"
                              name="confirmPassword"
                              className="form-control"
                              required
                              placeholder="Confirm new password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              disabled={loading || !token}
                              style={{ borderLeft: 'none', borderRight: 'none' }}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              disabled={loading || !token}
                              style={{ borderLeft: 'none' }}
                            >
                              <i className={`fas fa-${showConfirmPassword ? 'eye-slash' : 'eye'}`}></i>
                            </button>
                          </div>
                          {confirmPassword && newPassword !== confirmPassword && (
                            <small style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                              <i className="fas fa-times-circle me-1"></i>
                              Passwords do not match
                            </small>
                          )}
                          {confirmPassword && newPassword === confirmPassword && (
                            <small style={{ color: '#28a745', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                              <i className="fas fa-check-circle me-1"></i>
                              Passwords match
                            </small>
                          )}
                        </div>

                        <div className="d-grid mb-3">
                          <button
                            className="btn btn-primary btn-lg"
                            type="submit"
                            disabled={loading || !token || passwordErrors.length > 0 || newPassword !== confirmPassword}
                            style={{
                              backgroundColor: (loading || !token || passwordErrors.length > 0 || newPassword !== confirmPassword) ? '#cccccc' : '#4D64C1',
                              border: 'none',
                              padding: '12px',
                              fontSize: '16px',
                              fontWeight: '500',
                              borderRadius: '8px',
                              transition: 'all 0.3s ease',
                              cursor: (loading || !token || passwordErrors.length > 0 || newPassword !== confirmPassword) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Resetting Password...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-check me-2"></i> Reset Password
                              </>
                            )}
                          </button>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                          <Link
                            to="/login"
                            style={{
                              color: '#4D64C1',
                              textDecoration: 'none',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            <i className="fas fa-arrow-left me-2"></i>
                            Back to Login
                          </Link>
                        </div>
                      </form>
                    </div>
                  </div>
                ) : (
                  <div className="card" style={{
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    border: 'none',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      padding: '25px',
                      textAlign: 'center'
                    }}>
                      <i className="fas fa-check-circle" style={{ fontSize: '40px', marginBottom: '10px', opacity: 0.9 }}></i>
                      <h1 style={{ fontSize: '22px', margin: 0, fontWeight: '600' }}>Password Reset Successfully</h1>
                    </div>
                    <div className="card-body" style={{ padding: '35px 30px', textAlign: 'center' }}>
                      <p style={{ fontSize: '15px', color: '#2c3e50', marginBottom: '20px' }}>
                        Your password has been reset successfully!
                      </p>
                      <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '25px' }}>
                        You can now log in with your new password. Redirecting to login page...
                      </p>
                      <Link
                        to="/login"
                        className="btn btn-primary"
                        style={{
                          backgroundColor: '#4D64C1',
                          border: 'none',
                          padding: '12px 30px',
                          fontSize: '16px',
                          fontWeight: '500',
                          borderRadius: '8px'
                        }}
                      >
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Go to Login
                      </Link>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
